import { App } from 'vue'
import axios, { AxiosResponse, AxiosRequestConfig } from 'axios'
import store from '@/store/index'
import { Actions } from '@/store/enums/store.enums'
import AuthService from '@/core/services/AuthService'
import LocalStorageService from '@/core/services/LocalStorageService'
import VueAxios from 'vue-axios'

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
  /**
   * @description property to share vue instance
   */
  public static vueInstance: App

  private static isRefreshing = false
  private static failedQueue: any[] = []

  private static processQueue(error, token: string | null = null) {
    ApiService.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error)
      } else {
        prom.resolve(token)
      }
    })

    ApiService.failedQueue = []
  }

  /**
   * @description initialize vue axios
   */
  public static init(app: App<Element>) {
    const token = LocalStorageService.getToken('accessToken')
    store.dispatch(Actions.SET_ACCESS_TOKEN, token)
    if (token) {
      store.dispatch(Actions.SET_IS_LOGGED_IN)
    } else {
      store.dispatch(Actions.SET_IS_LOGGED_OUT)
    }
    ApiService.vueInstance = app
    ApiService.vueInstance.use(VueAxios, axios)
    ApiService.vueInstance.axios.defaults.baseURL = process.env.VUE_APP_HOST_API
    ApiService.vueInstance.axios.defaults.headers.common[
      'Authorization'
    ] = `Bearer ${token}`

    // Add interceptors
    ApiService.vueInstance.axios.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        const token = store.state['AuthModule'].accessToken
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`
        }
        return config
      },
      error => Promise.reject(error)
    )

    ApiService.vueInstance.axios.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config

        if (
          error.response &&
          error.response.status === 401 &&
          !originalRequest._retry
        ) {
          if (ApiService.isRefreshing) {
            return new Promise(function(resolve, reject) {
              ApiService.failedQueue.push({ resolve, reject })
            })
              .then(token => {
                originalRequest.headers['Authorization'] = 'Bearer ' + token
                return ApiService.vueInstance.axios(originalRequest)
              })
              .catch(err => {
                return Promise.reject(err)
              })
          }

          originalRequest._retry = true
          ApiService.isRefreshing = true

          return new Promise(function(resolve, reject) {
            AuthService.refreshToken()
              .then(success => {
                if (success) {
                  const token = store.state['AuthModule'].accessToken
                  ApiService.vueInstance.axios.defaults.headers.common[
                    'Authorization'
                  ] = `Bearer ${token}`
                  originalRequest.headers['Authorization'] = `Bearer ${token}`
                  ApiService.processQueue(null, token)
                  resolve(ApiService.vueInstance.axios(originalRequest))
                } else {
                  ApiService.processQueue(new Error('Refresh failed'), null)
                  AuthService.removeTokens()
                  reject(error)
                }
              })
              .catch(err => {
                ApiService.processQueue(err, null)
                AuthService.removeTokens()
                reject(err)
              })
              .finally(() => {
                ApiService.isRefreshing = false
              })
          })
        }

        // Handle other errors
        if (error.response && error.response.status) {
          if (error.response.status === 422) {
            return Promise.reject(error)
          } else if (error.response.status === 500) {
            return Promise.reject('Серверная ошибка')
          } else {
            return Promise.reject(`Ошибка ${error.response.status}`)
          }
        }
        return Promise.reject('Неопределённая ошибка')
      }
    )
  }

  /**
   * @description set the default HTTP request headers
   */
  public static setHeader(): void {
    ApiService.vueInstance.axios.defaults.headers.common[
      'Authorization'
    ] = `Bearer ${store.state['AuthModule'].accessToken}`
    ApiService.vueInstance.axios.defaults.baseURL = process.env.VUE_APP_HOST_API
  }

  /**
   * @description set the default HTTP request headers
   */
  public static setRefreshHeader(): void {
    ApiService.vueInstance.axios.defaults.headers.common[
      'Authorization'
    ] = `${LocalStorageService.getToken('accessToken')}`
    ApiService.vueInstance.axios.defaults.baseURL = process.env.VUE_APP_HOST_API
  }

  /**
   * @description set the default HTTP request headers
   */
  public static setHeaderMultipart(): void {
    ApiService.vueInstance.axios.defaults.headers.common['Content-Type'] = 'multipart/form-data'
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static query(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(resource, params)
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param slug: string
   * @returns Promise<AxiosResponse>
   */
  public static get(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(`${resource}`)
  }

  /**
   * @description set the POST HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static post(resource: string, params): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.post(`${resource}`, params)
  }

  /**
   * @description send the PATCH HTTP request
   * @param resource: string
   * @param params: object
   * @returns Promise<AxiosResponse>
   */
  public static patch(resource: string, params): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.patch(`${resource}`, params)
  }

  /**
   * @description send the GET HTTP request and get the response as a Blob (files, PDF)
   * @param resource: string
   * @returns Promise<Blob>
   */
  public static getBlob(resource: string): Promise<Blob> {
    return ApiService.vueInstance.axios
      .get(resource, { responseType: 'blob' })
      .then(response => response.data)
  }

  /**
   * @description send the UPDATE HTTP request
   * @param resource: string
   * @param slug: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static update(
    resource: string,
    slug: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}/${slug}`, params)
  }

  /**
   * @description Send the PUT HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static put(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}`, params)
  }

  /**
   * @description Send the DELETE HTTP request
   * @param resource: string
   * @returns Promise<AxiosResponse>
   */
  public static delete(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.delete(resource).catch(error => {
      throw new Error(`[RWV] ApiService ${error}`)
    })
  }
}

export default ApiService
